import Phaser from "phaser";
import { SCENARIOS } from "../config/campaign";
import { TOWERS } from "../config/towers";
import { MAPS } from "../config/map";
import {
  exportSaveData,
  importSaveData,
  loadSave,
  saveDiagnostics,
  writeSave,
} from "../systems/save";
import { getPlatformIdentity } from "../systems/admin-access";
import {
  getPlatformSyncDiagnostic,
  resetDefenseCampaign,
  subscribeToPlatformSync,
  synchronizeDefensePlatform,
} from "../systems/platform-integration";
import { progressionDiagnostics, totalStars } from "../systems/progression";
import { AudioManager } from "../systems/audio";
import { defenseAnalytics } from "../systems/analytics";
import { addButton, COLORS } from "./shared";

const ROYAL_BLUE = 0x2f5fa7;
const ROYAL_GOLD = 0xd8b45c;
const PARCHMENT = 0xf2e6c8;

export class MainMenuScene extends Phaser.Scene {
  private resizeHandler?: () => void;
  private administratorStatus?: Phaser.GameObjects.Container;

  constructor() {
    super("MainMenu");
  }

  create(): void {
    const save = loadSave();
    const audio = new AudioManager(save.settings);
    audio.bindUnlock(this);
    audio.playMusic("title");
    defenseAnalytics.track("main_menu_viewed", {
      completedLevels: save.progress.completedLevels.length,
    });
    this.cameras.main.setBackgroundColor(0x17243a);
    this.buildLivingCitadel(save.progress.completedLevels.length, totalStars(save.progress));
    this.buildRoyalLogo();
    this.buildCommandPanel();
    this.buildProgressSummary();
    this.buildAtmosphere();
    this.installPlatformSyncStatus();
    void this.installAdministratorDebugButton();

    this.resizeHandler = () => this.scene.restart();
    this.scale.once(Phaser.Scale.Events.RESIZE, this.resizeHandler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      if (this.resizeHandler) this.scale.off(Phaser.Scale.Events.RESIZE, this.resizeHandler);
    });
  }

  private installPlatformSyncStatus(): void {
    const status = this.add
      .text(14, this.scale.height - 12, "Campaign save: checking…", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "11px",
        color: "#e8d6ad",
        backgroundColor: "#211a16c8",
        padding: { x: 7, y: 4 },
      })
      .setOrigin(0, 1)
      .setDepth(81);
    const render = (): void => {
      const sync = getPlatformSyncDiagnostic();
      const labels = {
        idle: "waiting",
        syncing: "syncing…",
        synced: "cloud synced",
        "local-only": "local only — sign in for cloud save",
        offline: "offline — saved locally",
        "conflict-reconciled": "local + cloud progress combined",
        error: "cloud retry pending — saved locally",
      } as const;
      status.setText(`Campaign save: ${labels[sync.state]}`);
      status.setColor(
        sync.state === "synced" || sync.state === "conflict-reconciled" ? "#bff0aa" : "#e8d6ad",
      );
    };
    const unsubscribe = subscribeToPlatformSync(render);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, unsubscribe);
    render();
    const before = JSON.stringify(loadSave());
    void synchronizeDefensePlatform().then(() => {
      if (this.scene.isActive() && JSON.stringify(loadSave()) !== before) this.scene.restart();
    });
  }

  private buildLivingCitadel(completedLevels: number, stars: number): void {
    const width = this.scale.width;
    const height = this.scale.height;
    const horizon = height * 0.58;

    const sky = this.add.graphics().setDepth(-60);
    sky.fillGradientStyle(0x5d8cc0, 0x7fa9d0, 0xf0bf7a, 0xf5dbb1, 1);
    sky.fillRect(0, 0, width, horizon + 50);

    this.add
      .circle(width * 0.77, height * 0.18, Math.max(40, height * 0.07), 0xffdf9b, 0.72)
      .setDepth(-59);
    this.add
      .circle(width * 0.79, height * 0.2, Math.max(55, height * 0.09), 0xffe7bb, 0.08)
      .setDepth(-59);

    const mountainBack = this.add.graphics().setDepth(-56);
    mountainBack.fillStyle(0x71809a, 0.65);
    mountainBack.fillTriangle(-90, horizon, width * 0.18, height * 0.2, width * 0.4, horizon);
    mountainBack.fillTriangle(
      width * 0.16,
      horizon,
      width * 0.48,
      height * 0.12,
      width * 0.73,
      horizon,
    );
    mountainBack.fillStyle(0x55667d, 0.72);
    mountainBack.fillTriangle(
      width * 0.56,
      horizon,
      width * 0.78,
      height * 0.25,
      width + 110,
      horizon,
    );

    const mountainFront = this.add.graphics().setDepth(-54);
    mountainFront.fillStyle(0x7e8ca2, 0.45);
    mountainFront.fillTriangle(
      width * 0.58,
      horizon,
      width * 0.73,
      height * 0.32,
      width * 0.88,
      horizon,
    );
    mountainFront.fillTriangle(
      width * 0.04,
      horizon,
      width * 0.18,
      height * 0.27,
      width * 0.34,
      horizon,
    );

    for (let index = 0; index < 6; index += 1) {
      const cloud = this.add.container(
        Phaser.Math.Between(-100, Math.max(100, width - 100)),
        Phaser.Math.Between(50, Math.max(90, Math.floor(height * 0.28))),
      );
      const alpha = Phaser.Math.FloatBetween(0.12, 0.26);
      cloud.add([
        this.add.ellipse(0, 0, 120, 30, 0xffffff, alpha),
        this.add.ellipse(-34, -8, 66, 30, 0xffffff, alpha),
        this.add.ellipse(30, -10, 78, 34, 0xffffff, alpha),
      ]);
      cloud.setDepth(-52).setScale(Phaser.Math.FloatBetween(0.75, 1.5));
      this.tweens.add({
        targets: cloud,
        x: width + 180,
        duration: Phaser.Math.Between(50000, 85000),
        repeat: -1,
        onRepeat: () => {
          cloud.x = -180;
          cloud.y = Phaser.Math.Between(50, Math.max(90, Math.floor(height * 0.28)));
        },
      });
    }

    for (let index = 0; index < 4; index += 1) {
      const startY = Phaser.Math.Between(85, Math.max(100, Math.floor(height * 0.24)));
      const bird = this.add
        .container(Phaser.Math.Between(-40, Math.max(40, width - 220)), startY)
        .setDepth(-51);

      const leftWing = this.add.container(0, 0);
      const leftShape = this.add.graphics();
      leftShape.lineStyle(2, 0x2e3641, 0.95);
      leftShape.beginPath();
      leftShape.moveTo(0, 0);
      leftShape.lineTo(-4, -3);
      leftShape.lineTo(-8, -5);
      leftShape.lineTo(-12, -2);
      leftShape.lineTo(-16, 2);
      leftShape.strokePath();
      leftWing.add(leftShape);

      const rightWing = this.add.container(0, 0);
      const rightShape = this.add.graphics();
      rightShape.lineStyle(2, 0x2e3641, 0.95);
      rightShape.beginPath();
      rightShape.moveTo(0, 0);
      rightShape.lineTo(4, -3);
      rightShape.lineTo(8, -5);
      rightShape.lineTo(12, -2);
      rightShape.lineTo(16, 2);
      rightShape.strokePath();
      rightWing.add(rightShape);

      bird.add([leftWing, rightWing]);
      bird.setScale(Phaser.Math.FloatBetween(0.8, 1.4));

      this.tweens.add({
        targets: leftWing,
        angle: { from: -12, to: 18 },
        scaleY: { from: 0.75, to: 1.2 },
        duration: 260,
        ease: "Sine.InOut",
        yoyo: true,
        repeat: -1,
      });
      this.tweens.add({
        targets: rightWing,
        angle: { from: 12, to: -18 },
        scaleY: { from: 0.75, to: 1.2 },
        duration: 260,
        ease: "Sine.InOut",
        yoyo: true,
        repeat: -1,
      });
      this.tweens.add({
        targets: bird,
        x: width + 120,
        duration: Phaser.Math.Between(18000, 32000),
        repeat: -1,
        delay: Phaser.Math.Between(0, 7000),
        onRepeat: () => {
          bird.x = -120;
          bird.y = Phaser.Math.Between(85, Math.max(100, Math.floor(height * 0.24)));
        },
      });
      this.tweens.add({
        targets: bird,
        y: startY - 6,
        duration: 900,
        ease: "Sine.InOut",
        yoyo: true,
        repeat: -1,
      });
    }

    this.add
      .rectangle(0, horizon - 18, width, height - horizon + 18, 0x4f7444)
      .setOrigin(0)
      .setDepth(-48);
    this.add
      .ellipse(width * 0.25, horizon + 6, width * 0.95, height * 0.28, 0x6b9252)
      .setDepth(-47);
    this.add
      .ellipse(width * 0.77, horizon + 18, width * 0.84, height * 0.26, 0x62884c)
      .setDepth(-47);
    this.add
      .ellipse(width * 0.15, height * 0.9, width * 0.68, height * 0.18, 0x557944)
      .setDepth(-46);
    this.add
      .ellipse(width * 0.78, height * 0.92, width * 0.72, height * 0.2, 0x4c6f3e)
      .setDepth(-46);

    const castleScale = Phaser.Math.Clamp(Math.min(width / 1280, height / 720), 0.72, 1.25);
    const castle = this.add
      .container(width * 0.5, horizon - 24)
      .setScale(castleScale)
      .setDepth(-30);
    const stone = 0x827f78;
    const stoneLight = 0xaaa59a;
    const stoneMid = 0x979289;
    const stoneDark = 0x4e4d49;

    const wall = this.add.rectangle(0, 42, 540, 176, stone).setStrokeStyle(6, stoneDark);
    const keep = this.add.rectangle(0, -62, 232, 286, stoneLight).setStrokeStyle(6, stoneDark);
    const leftTower = this.add.rectangle(-252, -8, 120, 280, stoneMid).setStrokeStyle(6, stoneDark);
    const rightTower = this.add.rectangle(252, -8, 120, 280, stoneMid).setStrokeStyle(6, stoneDark);
    const keepShadow = this.add.rectangle(22, -34, 192, 246, 0x000000, 0.08);
    castle.add([wall, keepShadow, keep, leftTower, rightTower]);

    const stoneDetails = this.add.graphics();
    stoneDetails.fillStyle(0xb8b3a9, 0.94);
    for (let x = -302; x <= 302; x += 42) stoneDetails.fillRect(x, -56, 24, 42);
    for (let x = -108; x <= 108; x += 42) stoneDetails.fillRect(x, -220, 24, 42);
    stoneDetails.fillStyle(0x66625c, 1).fillRoundedRect(-42, 52, 84, 114, 28);
    stoneDetails.lineStyle(4, 0x433932, 0.95).strokeRoundedRect(-42, 52, 84, 114, 28);
    stoneDetails.lineStyle(3, 0x685f56, 0.55);
    for (let y = -172; y <= 92; y += 36) {
      stoneDetails.lineBetween(-108, y, 108, y);
    }
    for (let y = -100; y <= 90; y += 40) {
      stoneDetails.lineBetween(-302, y, -202, y);
      stoneDetails.lineBetween(202, y, 302, y);
    }
    castle.add(stoneDetails);

    const roof = this.add.graphics();
    roof.fillStyle(0x365a8a, 1);
    roof.fillTriangle(-318, -146, -252, -248, -186, -146);
    roof.fillTriangle(186, -146, 252, -248, 318, -146);
    roof.fillTriangle(-128, -220, 0, -334, 128, -220);
    roof.lineStyle(4, 0x274061, 1);
    roof.strokeTriangle(-318, -146, -252, -248, -186, -146);
    roof.strokeTriangle(186, -146, 252, -248, 318, -146);
    roof.strokeTriangle(-128, -220, 0, -334, 128, -220);
    castle.add(roof);

    const flags: Phaser.GameObjects.Container[] = [];
    for (const x of [-252, 0, 252]) {
      const flag = this.add.container(x, x === 0 ? -328 : -238);
      const pole = this.add.rectangle(0, 24, 4, 76, 0x4b3424);
      const cloth = this.add.polygon(22, 0, [0, 0, 52, 11, 0, 25], ROYAL_BLUE).setOrigin(0, 0);
      const pennant = this.add.polygon(18, 18, [0, 0, 28, 6, 0, 16], 0x4a73ba).setOrigin(0, 0);
      flag.add([pole, cloth, pennant]);
      castle.add(flag);
      flags.push(flag);
      this.tweens.add({
        targets: [cloth, pennant],
        scaleX: 0.76,
        duration: 520,
        yoyo: true,
        repeat: -1,
      });
      this.tweens.add({
        targets: [cloth, pennant],
        angle: 2.5,
        duration: 620,
        yoyo: true,
        repeat: -1,
      });
    }

    const windows: Phaser.GameObjects.Rectangle[] = [];
    for (const [x, y] of [
      [-52, -100],
      [52, -100],
      [-52, -22],
      [52, -22],
      [-252, -30],
      [252, -30],
    ]) {
      const window = this.add.rectangle(x, y, 18, 32, 0xf7c95d, 0.72).setStrokeStyle(3, stoneDark);
      castle.add(window);
      windows.push(window);
    }
    windows.forEach((window, index) => {
      this.tweens.add({
        targets: window,
        alpha: { from: 0.56, to: 0.95 },
        duration: 900 + index * 120,
        yoyo: true,
        repeat: -1,
      });
    });

    const portcullis = this.add.graphics();
    portcullis.fillStyle(0x47392e, 1).fillRoundedRect(-54, 50, 108, 36, 11);
    portcullis.lineStyle(3, 0x291f18, 1).strokeRoundedRect(-54, 50, 108, 36, 11);
    for (let x = -42; x <= 42; x += 14) portcullis.lineBetween(x, 52, x, 86);
    portcullis.lineBetween(-54, 63, 54, 63);
    castle.add(portcullis);

    const progressionTier = Phaser.Math.Clamp(Math.floor((completedLevels + stars) / 10), 0, 4);
    const cottageZones: { x: number; y: number; width: number; height: number }[] = [];
    const cottageLayouts = [
      { x: width * 0.1, y: horizon + 178 },
      { x: width * 0.9, y: horizon + 178 },
      { x: width * 0.18, y: horizon + 232 },
      { x: width * 0.82, y: horizon + 232 },
    ];
    for (let index = 0; index < progressionTier; index += 1) {
      const layout = cottageLayouts[index];
      if (!layout) continue;
      const houseWidth = 92 * castleScale;
      const houseHeight = 72 * castleScale;
      const roofHeight = 42 * castleScale;
      const house = this.add.container(layout.x, layout.y).setDepth(-25);
      const bodyColor = index % 2 === 0 ? 0x76543c : 0x6d655d;
      const body = this.add
        .rectangle(0, 0, houseWidth, houseHeight, bodyColor)
        .setStrokeStyle(3, 0x4b392d);
      const roof = this.add
        .triangle(
          0,
          -houseHeight / 2,
          -houseWidth * 0.62,
          0,
          houseWidth * 0.62,
          0,
          0,
          -roofHeight,
          0x7b342d,
        )
        .setStrokeStyle(3, 0x51241f);
      const door = this.add.rectangle(
        0,
        houseHeight * 0.18,
        houseWidth * 0.2,
        houseHeight * 0.46,
        0x3d2c23,
      );
      const window = this.add
        .rectangle(houseWidth * 0.27, -4, houseWidth * 0.18, houseHeight * 0.22, 0xf4ca66, 0.75)
        .setStrokeStyle(2, 0x5a4632);
      house.add([body, roof, door, window]);
      cottageZones.push({
        x: layout.x - houseWidth * 0.7,
        y: layout.y - houseHeight / 2 - roofHeight,
        width: houseWidth * 1.4,
        height: houseHeight + roofHeight,
      });
    }

    const moat = this.add.graphics().setDepth(-29);
    const moatTop = horizon + 112;
    moat.fillStyle(0x356f8b, 1);
    moat.fillEllipse(width * 0.5, moatTop + 24, width * 0.42, 52);
    moat.fillStyle(0x5599b6, 0.9).fillEllipse(width * 0.5, moatTop + 20, width * 0.34, 28);

    for (let index = 0; index < 5; index += 1) {
      const ripple = this.add
        .ellipse(
          width * 0.5 + Phaser.Math.Between(-Math.floor(width * 0.13), Math.floor(width * 0.13)),
          moatTop + Phaser.Math.Between(14, 28),
          Phaser.Math.Between(34, 72),
          Phaser.Math.Between(4, 8),
          0xc9edf3,
          Phaser.Math.FloatBetween(0.16, 0.34),
        )
        .setDepth(-28);
      this.tweens.add({
        targets: ripple,
        scaleX: { from: 0.72, to: 1.28 },
        alpha: { from: ripple.alpha, to: 0 },
        duration: Phaser.Math.Between(1800, 3100),
        delay: Phaser.Math.Between(0, 1500),
        repeat: -1,
        onRepeat: () => {
          ripple.x =
            width * 0.5 + Phaser.Math.Between(-Math.floor(width * 0.13), Math.floor(width * 0.13));
          ripple.y = moatTop + Phaser.Math.Between(14, 28);
        },
      });
    }

    const bridge = this.add.graphics().setDepth(-27);
    const bridgeWidth = width * 0.14;
    const bridgeY = horizon + 94;
    bridge.fillStyle(0x7f6040, 1).fillRoundedRect(width * 0.43, bridgeY, bridgeWidth, 34, 10);
    bridge
      .fillStyle(0x8e6d48, 1)
      .fillRoundedRect(width * 0.44, bridgeY + 2, bridgeWidth - 24, 30, 8);
    bridge
      .lineStyle(4, 0x4e3928, 0.9)
      .strokeRoundedRect(width * 0.43, bridgeY, bridgeWidth, 34, 10);
    for (let x = width * 0.448; x < width * 0.43 + bridgeWidth - 12; x += 18) {
      bridge.lineBetween(x, bridgeY + 4, x, bridgeY + 30);
    }

    const road = this.add.graphics().setDepth(-26);
    const roadStartY = bridgeY + 34;
    road.fillStyle(0x8f7b60, 0.95);
    road.beginPath();
    road.moveTo(width * 0.458, roadStartY);
    road.lineTo(width * 0.542, roadStartY);
    road.lineTo(width * 0.64, height);
    road.lineTo(width * 0.36, height);
    road.closePath();
    road.fillPath();
    road.fillStyle(0xa89579, 0.45);
    road.beginPath();
    road.moveTo(width * 0.478, roadStartY + 14);
    road.lineTo(width * 0.522, roadStartY + 14);
    road.lineTo(width * 0.595, height);
    road.lineTo(width * 0.405, height);
    road.closePath();
    road.fillPath();
    road.lineStyle(2, 0x66503d, 0.34);
    for (let y = roadStartY + 46; y < height; y += 26) {
      const left = Phaser.Math.Linear(
        width * 0.46,
        width * 0.36,
        (y - roadStartY) / (height - roadStartY),
      );
      const right = Phaser.Math.Linear(
        width * 0.54,
        width * 0.64,
        (y - roadStartY) / (height - roadStartY),
      );
      road.lineBetween(left, y, right, y + Phaser.Math.Between(-2, 2));
    }
    road.lineStyle(4, 0x6d5641, 0.4);
    road.lineBetween(width * 0.435, roadStartY + 46, width * 0.415, height - 16);
    road.lineBetween(width * 0.565, roadStartY + 46, width * 0.585, height - 16);

    road.lineStyle(2, 0x5f4a39, 0.28);
    for (let row = 0; row < 9; row += 1) {
      const t = row / 8;
      const y = Phaser.Math.Linear(roadStartY + 24, height - 28, t);
      const left = Phaser.Math.Linear(width * 0.463, width * 0.37, t);
      const right = Phaser.Math.Linear(width * 0.537, width * 0.63, t);
      const columns = 3 + Math.floor(t * 3);
      for (let column = 1; column < columns; column += 1) {
        const x = Phaser.Math.Linear(left, right, column / columns);
        road.lineBetween(x, y - 8, x + Phaser.Math.Between(-4, 4), y + 8);
      }
    }

    const shrubs = this.add.graphics().setDepth(-24);
    for (let index = 0; index < 14; index += 1) {
      const x = Phaser.Math.Between(18, Math.max(28, width - 18));
      const y = Phaser.Math.Between(Math.floor(horizon + 60), height - 24);
      if (x > width * 0.32 && x < width * 0.68 && y > horizon + 30) continue;
      if (
        cottageZones.some(
          (zone) =>
            x >= zone.x - 24 &&
            x <= zone.x + zone.width + 24 &&
            y >= zone.y - 24 &&
            y <= zone.y + zone.height + 24,
        )
      )
        continue;
      shrubs.fillStyle(Phaser.Math.RND.pick([0x406e3d, 0x4f7f47, 0x5a8d50]), 1);
      shrubs.fillCircle(x, y, Phaser.Math.Between(6, 12));
      shrubs.fillStyle(0xb58b4d, 0.45);
      if (Phaser.Math.Between(0, 3) === 0) shrubs.fillCircle(x + 4, y + 2, 2);
    }

    for (let index = 0; index < Math.max(9, Math.floor(width / 135)); index += 1) {
      const treeX = Phaser.Math.Between(20, Math.max(30, width - 20));
      const treeY = Phaser.Math.Between(
        Math.floor(horizon + 42),
        Math.max(Math.floor(horizon + 54), height - 26),
      );
      if (treeX > width * 0.34 && treeX < width * 0.67) continue;
      if (
        cottageZones.some(
          (zone) =>
            treeX >= zone.x - 28 &&
            treeX <= zone.x + zone.width + 28 &&
            treeY >= zone.y - 34 &&
            treeY <= zone.y + zone.height + 24,
        )
      )
        continue;
      const tree = this.add.container(treeX, treeY).setDepth(-23 + treeY / height);
      tree.add([
        this.add.rectangle(0, 13, 8, 36, 0x63462f),
        this.add.circle(0, -10, 22, Phaser.Math.RND.pick([0x315d36, 0x3f7040, 0x527d43])),
        this.add.circle(-10, -4, 11, Phaser.Math.RND.pick([0x3b6c3f, 0x537f48])),
        this.add.circle(11, -2, 12, Phaser.Math.RND.pick([0x365f37, 0x4a7842])),
      ]);
      this.tweens.add({
        targets: tree,
        angle: { from: -1.2, to: 1.2 },
        duration: Phaser.Math.Between(1900, 3200),
        yoyo: true,
        repeat: -1,
      });
    }

    const smokeStacks = [
      { x: -430, y: 46 },
      { x: 430, y: 48 },
    ];
    smokeStacks.forEach((stack) => {
      for (let puff = 0; puff < 3; puff += 1) {
        const cloud = this.add
          .circle(
            width * 0.5 + stack.x * castleScale,
            horizon + stack.y * castleScale,
            12 + puff * 4,
            0xe7e0d4,
            0.28,
          )
          .setDepth(-22);
        this.tweens.add({
          targets: cloud,
          y: cloud.y - 48,
          x: cloud.x + 18,
          alpha: 0,
          scaleX: 1.25,
          scaleY: 1.18,
          duration: 2800 + puff * 240,
          delay: puff * 560,
          repeat: -1,
        });
      }
    });

    const gateGlow = this.add
      .ellipse(width * 0.5, bridgeY - 18, bridgeWidth * 0.68, 46, 0xf6c45c, 0.08)
      .setDepth(-26);
    this.tweens.add({
      targets: gateGlow,
      alpha: { from: 0.05, to: 0.15 },
      scaleX: { from: 0.94, to: 1.06 },
      scaleY: { from: 0.92, to: 1.08 },
      duration: 1500,
      ease: "Sine.InOut",
      yoyo: true,
      repeat: -1,
    });

    const torchContainer = this.add.container(width * 0.5, bridgeY + 9).setDepth(-25);
    for (const offset of [-bridgeWidth * 0.54, bridgeWidth * 0.54]) {
      const post = this.add.rectangle(offset, -8, 5, 26, 0x4e3825);
      const flameCore = this.add.circle(offset, -20, 6, 0xffec92, 0.9);
      const flameOuter = this.add.circle(offset, -20, 10, 0xf39d2c, 0.42);
      torchContainer.add([post, flameOuter, flameCore]);
      this.tweens.add({
        targets: [flameCore, flameOuter],
        scaleX: 0.82,
        scaleY: 1.24,
        duration: 260,
        yoyo: true,
        repeat: -1,
      });
      this.tweens.add({
        targets: [flameCore, flameOuter],
        alpha: { from: 0.62, to: 1 },
        duration: 180,
        yoyo: true,
        repeat: -1,
      });
    }

    const lightSweep = this.add
      .rectangle(width * 0.5 - 90, horizon - 118, 18, 250, 0xffefc5, 0.025)
      .setAngle(18)
      .setDepth(-29);
    this.tweens.add({
      targets: lightSweep,
      x: width * 0.5 + 120,
      alpha: { from: 0.015, to: 0.055 },
      duration: 7200,
      ease: "Sine.InOut",
      yoyo: true,
      repeat: -1,
    });

    this.tweens.add({
      targets: castle,
      x: castle.x + 6,
      y: castle.y - 3,
      duration: 9000,
      ease: "Sine.InOut",
      yoyo: true,
      repeat: -1,
    });
  }

  private buildRoyalLogo(): void {
    const width = this.scale.width;
    const compact = width < 850;
    const x = compact ? width / 2 : width * 0.28;
    const y = compact ? 92 : 112;
    const logo = this.add.container(x, y).setDepth(40);

    const shadow = this.add.ellipse(0, 54, compact ? 390 : 500, 80, 0x000000, 0.28);
    const plaque = this.add.graphics();
    const plaqueWidth = compact ? 420 : 540;
    plaque.fillStyle(0x27211c, 0.94).fillRoundedRect(-plaqueWidth / 2, -70, plaqueWidth, 150, 24);
    plaque.lineStyle(5, 0x8c6826, 1).strokeRoundedRect(-plaqueWidth / 2, -70, plaqueWidth, 150, 24);
    plaque
      .lineStyle(2, ROYAL_GOLD, 0.9)
      .strokeRoundedRect(-plaqueWidth / 2 + 9, -61, plaqueWidth - 18, 132, 18);
    plaque
      .fillStyle(ROYAL_BLUE, 0.95)
      .fillTriangle(-plaqueWidth / 2, -48, -plaqueWidth / 2 + 82, -48, -plaqueWidth / 2, 36);
    plaque
      .fillStyle(ROYAL_BLUE, 0.95)
      .fillTriangle(plaqueWidth / 2, -48, plaqueWidth / 2 - 82, -48, plaqueWidth / 2, 36);

    const crest = this.add
      .polygon(0, -45, [0, -34, 29, -15, 21, 27, 0, 42, -21, 27, -29, -15], ROYAL_BLUE)
      .setStrokeStyle(4, ROYAL_GOLD);
    const crown = this.add
      .text(0, -49, "♛", {
        fontFamily: "Georgia, serif",
        fontSize: "33px",
        color: "#f2d27a",
      })
      .setOrigin(0.5);
    const name = this.add
      .text(0, 10, "LITTLECASTLE", {
        fontFamily: "Georgia, serif",
        fontSize: compact ? "31px" : "40px",
        fontStyle: "bold",
        color: "#fff3cf",
        stroke: "#160f0b",
        strokeThickness: 5,
        shadow: { offsetX: 0, offsetY: 4, color: "#000000", blur: 5, fill: true },
      })
      .setOrigin(0.5);
    const subtitle = this.add
      .text(0, 52, "D E F E N S E", {
        fontFamily: "Georgia, serif",
        fontSize: compact ? "17px" : "20px",
        fontStyle: "bold",
        color: "#e4bd5f",
      })
      .setOrigin(0.5);

    logo.add([shadow, plaque, crest, crown, name, subtitle]);
    logo.setAlpha(0).setScale(0.88);
    this.tweens.add({
      targets: logo,
      alpha: 1,
      scaleX: 1,
      scaleY: 1,
      y: y + 5,
      duration: 650,
      ease: "Back.Out",
    });
  }

  private buildCommandPanel(): void {
    const width = this.scale.width;
    const height = this.scale.height;
    const compact = width < 850;
    const x = compact ? width / 2 : width * 0.78;
    const itemCount = compact ? 4 : 7;
    const panelWidth = compact ? Math.min(420, width - 28) : Math.min(390, width * 0.34);
    const availableHeight = Math.max(250, height - 170);
    const panelHeight = Math.min(compact ? 300 : 430, availableHeight);
    const y = compact
      ? Phaser.Math.Clamp(height * 0.63, panelHeight / 2 + 150, height - panelHeight / 2 - 12)
      : Phaser.Math.Clamp(height * 0.52, panelHeight / 2 + 16, height - panelHeight / 2 - 16);

    const panel = this.add.container(x, y).setDepth(50);
    const shadow = this.add.rectangle(8, 12, panelWidth, panelHeight, 0x000000, 0.34);
    const stone = this.add
      .rectangle(0, 0, panelWidth, panelHeight, 0x302a25, 0.95)
      .setStrokeStyle(5, 0x8f7138);
    const parchment = this.add
      .rectangle(0, 0, panelWidth - 24, panelHeight - 24, 0x594b3b, 0.74)
      .setStrokeStyle(2, ROYAL_GOLD, 0.55);
    const heading = this.add
      .text(0, -panelHeight / 2 + 34, "ROYAL COMMAND", {
        fontFamily: "Georgia, serif",
        fontSize: "18px",
        fontStyle: "bold",
        color: "#e8c86d",
      })
      .setOrigin(0.5);
    panel.add([shadow, stone, parchment, heading]);

    const buttonWidth = panelWidth - 66;
    const topPadding = compact ? 78 : 74;
    const bottomPadding = compact ? 24 : 30;
    const buttonHalfHeight = 25;
    const usableCenters = panelHeight - topPadding - bottomPadding - buttonHalfHeight;
    const gap = Math.min(compact ? 49 : 50, usableCenters / Math.max(1, itemCount - 1));
    const startY = y - panelHeight / 2 + topPadding;
    const save = loadSave();
    const hasProgress = save.progress.completedLevels.length > 0;

    addButton(
      this,
      x,
      startY,
      buttonWidth,
      hasProgress ? "Continue Campaign" : "Begin Campaign",
      () => {
        this.scene.start("LevelSelect");
      },
      true,
    ).setDepth(55);
    addButton(this, x, startY + gap, buttonWidth, "Campaign Map", () =>
      this.scene.start("LevelSelect"),
    ).setDepth(55);
    addButton(this, x, startY + gap * 2, buttonWidth, "Kingdom Progress", () =>
      this.scene.start("Progression"),
    ).setDepth(55);
    addButton(this, x, startY + gap * 3, buttonWidth, "Multiplayer", () =>
      this.scene.start("MultiplayerLobby"),
    ).setDepth(55);
    if (!compact) {
      addButton(this, x, startY + gap * 4, buttonWidth, "How to Defend", () =>
        this.showHelp(),
      ).setDepth(55);
      addButton(this, x, startY + gap * 5, buttonWidth, "Settings", () =>
        this.showSettings(),
      ).setDepth(55);
      addButton(this, x, startY + gap * 6, buttonWidth, "Return to Games", () => {
        window.location.href = "/";
      }).setDepth(55);
    }
  }

  private buildProgressSummary(): void {
    const save = loadSave();
    const scenario = SCENARIOS.find((entry) => entry.id === save.progress.lastScenarioId);
    const map = scenario ? MAPS[scenario.mapId] : undefined;
    const completed = save.progress.completedLevels.length;
    const stars = totalStars(save.progress);
    const width = this.scale.width;
    const height = this.scale.height;
    const compact = width < 850;
    const x = compact ? width / 2 : width * 0.28;
    const y = compact ? Math.min(height - 42, 235) : height - 92;
    const label =
      completed > 0
        ? `Last command: ${map?.displayName ?? "The Kingdom"} • ${scenario?.displayName ?? "Campaign"}\n${completed} victories  •  ${stars} campaign stars  •  ${save.progress.royalSeals} royal seals`
        : "The kingdom awaits its first commander.\nChoose Begin Campaign and hold the road.";

    const panel = this.add
      .rectangle(x, y, compact ? Math.min(500, width - 30) : 560, 72, 0x1e1916, 0.82)
      .setStrokeStyle(2, ROYAL_GOLD, 0.52)
      .setDepth(45);
    const text = this.add
      .text(x, y, label, {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: compact ? "12px" : "14px",
        color: "#f2e6c8",
        align: "center",
        lineSpacing: 6,
      })
      .setOrigin(0.5)
      .setDepth(46);
    panel.setAlpha(0);
    text.setAlpha(0);
    this.tweens.add({ targets: [panel, text], alpha: 1, duration: 700, delay: 320 });
  }

  private buildAtmosphere(): void {
    const width = this.scale.width;
    const height = this.scale.height;
    for (let index = 0; index < 28; index += 1) {
      const mote = this.add
        .circle(
          Phaser.Math.Between(20, Math.max(30, width - 20)),
          Phaser.Math.Between(
            Math.floor(height * 0.35),
            Math.max(Math.floor(height * 0.4), height - 20),
          ),
          Phaser.Math.FloatBetween(0.8, 2.1),
          Phaser.Math.RND.pick([0xf1d27a, 0xffffff, 0xcde4a3]),
          Phaser.Math.FloatBetween(0.12, 0.32),
        )
        .setDepth(20);
      this.tweens.add({
        targets: mote,
        x: mote.x + Phaser.Math.Between(-35, 35),
        y: mote.y - Phaser.Math.Between(35, 110),
        alpha: 0,
        duration: Phaser.Math.Between(2600, 5600),
        delay: Phaser.Math.Between(0, 1800),
        repeat: -1,
      });
    }

    const version = this.add
      .text(width - 14, height - 12, "LittleCastle Games  •  Defense v2.0", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "11px",
        color: "#d8ccb4",
      })
      .setOrigin(1, 1)
      .setDepth(80)
      .setAlpha(0.72);
    version.setShadow(0, 1, "#000000", 2, true, true);
  }

  private async installAdministratorDebugButton(forceRefresh = false): Promise<void> {
    this.administratorStatus?.destroy(true);
    const container = this.add.container(0, 0).setDepth(92);
    this.administratorStatus = container;
    const x = Math.max(122, this.scale.width - 170);
    const y = 74;
    const checking = this.add
      .text(x, y, "Checking LittleCastle account…", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "12px",
        color: "#fff3cf",
        backgroundColor: "#211a16e8",
        padding: { x: 9, y: 6 },
      })
      .setOrigin(0.5);
    container.add(checking);

    const identity = await getPlatformIdentity(forceRefresh);
    if (!this.scene.isActive() || container !== this.administratorStatus) return;
    container.removeAll(true);
    const statusLabel = identity.administrator
      ? `Admin verified${identity.displayName ? `: ${identity.displayName}` : ""}`
      : identity.authenticated
        ? "Signed in — administrator role not found"
        : `Admin check: ${identity.status.replaceAll("-", " ")}`;
    const status = this.add
      .text(x, y, statusLabel, {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "11px",
        color: identity.administrator ? "#bff0aa" : "#f0d19a",
        backgroundColor: "#211a16e8",
        padding: { x: 9, y: 6 },
        align: "center",
        wordWrap: { width: 290 },
      })
      .setOrigin(0.5);
    container.add(status);

    if (identity.authenticated && identity.administrator) {
      this.registry.set("defense-administrator", true);
      const button = addButton(
        this,
        x,
        y + 42,
        166,
        "Administrator Debug",
        () => this.showAdministratorDebugMenu(identity.displayName),
        true,
      );
      container.add(button);
      return;
    }

    const retry = addButton(this, x, y + 42, 120, "Retry Check", () => {
      void this.installAdministratorDebugButton(true);
    });
    container.add(retry);
    status.setInteractive({ useHandCursor: true });
    status.on("pointerdown", () => {
      console.info("[Defense admin access] current diagnostic", identity);
      status.setText(`${statusLabel}
${identity.detail}`);
    });
  }

  private showAdministratorDebugMenu(displayName: string | null): void {
    const center = this.scale.width / 2;
    const middle = this.scale.height / 2;
    const width = Math.min(620, this.scale.width - 30);
    const height = Math.min(520, this.scale.height - 30);
    const shade = this.add
      .rectangle(center, middle, this.scale.width, this.scale.height, 0x000000, 0.78)
      .setInteractive()
      .setDepth(150);
    const panel = this.add
      .rectangle(center, middle, width, height, 0x211a16, 0.99)
      .setStrokeStyle(4, ROYAL_GOLD)
      .setDepth(151);
    const heading = this.add
      .text(center, middle - height / 2 + 38, "ADMINISTRATOR DEBUG", {
        fontFamily: "Georgia, serif",
        fontSize: "25px",
        fontStyle: "bold",
        color: "#fff3cf",
      })
      .setOrigin(0.5)
      .setDepth(152);
    const subtitle = this.add
      .text(
        center,
        middle - height / 2 + 72,
        displayName ? `Authorized as ${displayName}` : "Administrator access verified",
        {
          fontFamily: "Trebuchet MS, sans-serif",
          fontSize: "13px",
          color: "#e0c77f",
        },
      )
      .setOrigin(0.5)
      .setDepth(152);
    const status = this.add
      .text(center, middle + height / 2 - 74, "Debug changes affect this browser save.", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "12px",
        color: "#c9baa2",
        align: "center",
        wordWrap: { width: width - 38 },
      })
      .setOrigin(0.5)
      .setDepth(152);
    const controls: Phaser.GameObjects.GameObject[] = [shade, panel, heading, subtitle, status];
    const buttonWidth = Math.min(390, width - 44);
    const startY = middle - height / 2 + 124;
    const gap = 54;
    const action = (label: string, row: number, callback: () => void): void => {
      controls.push(
        addButton(this, center, startY + gap * row, buttonWidth, label, callback, true).setDepth(
          153,
        ),
      );
    };

    action("Unlock All Towers", 0, () => {
      const save = loadSave();
      writeSave({
        ...save,
        progress: { ...save.progress, unlockedTowers: Object.keys(TOWERS) },
        loadout: { ...save.loadout, towerIds: Object.keys(TOWERS).slice(0, 5) },
      });
      status.setText(`Unlocked all ${Object.keys(TOWERS).length} tower companies.`);
    });
    action("Unlock All Campaign Scenarios", 1, () => {
      const save = loadSave();
      writeSave({
        ...save,
        progress: {
          ...save.progress,
          completedLevels: SCENARIOS.map((scenario) => scenario.id),
          scenarioStars: Object.fromEntries(SCENARIOS.map((scenario) => [scenario.id, 3])),
        },
      });
      status.setText("All campaign scenarios and stars unlocked.");
    });
    action("Grant 99,999 Royal Seals", 2, () => {
      const save = loadSave();
      writeSave({
        ...save,
        progress: {
          ...save.progress,
          royalSeals: (save.progress.royalSeals ?? 0) + 99999,
          progressionCurrency: save.progress.progressionCurrency + 99999,
        },
      });
      status.setText("Granted debug progression currency.");
    });
    action("Repair and Diagnose Progression", 3, () => {
      const save = loadSave();
      writeSave(save);
      const report = progressionDiagnostics(save.progress);
      const storage = saveDiagnostics();
      const platform = getPlatformSyncDiagnostic();
      console.info("[Defense progression diagnostic]", { report, storage, platform });
      status.setText(
        `Completed ${report.completedScenarioIds.length}/${SCENARIOS.length} • Unlocked ${report.unlockedScenarioIds.length}/${SCENARIOS.length} • Towers ${report.unlockedTowerIds.length}/${Object.keys(TOWERS).length}\nFirst victory → second scenario: ${report.firstScenarioUnlocksSecond ? "PASS" : "FAIL"} • Save v${storage.schemaVersion}/r${storage.localRevision} • Cloud v${storage.cloudVersion} ${storage.dirty ? "pending" : "clean"}`,
      );
    });

    let resetArmed = false;
    const reset = addButton(
      this,
      center,
      startY + gap * 4,
      buttonWidth,
      "Reset All Progress",
      () => {
        if (!resetArmed) {
          resetArmed = true;
          const label = reset.getAt(1);
          if (label instanceof Phaser.GameObjects.Text) label.setText("Confirm Complete Reset");
          status.setText("Press again to permanently clear this browser save.");
          return;
        }
        status.setText("Resetting local and cloud campaign saves…");
        void resetDefenseCampaign().then((result) => {
          if (result.ok) window.location.reload();
          else status.setText(result.detail);
        });
      },
      true,
    ).setDepth(153);
    controls.push(reset);

    const close = addButton(this, center, middle + height / 2 - 34, 170, "Close", () => {
      controls.forEach((item) => item.destroy());
      close.destroy();
    }).setDepth(153);
    controls.push(close);
  }

  private showHelp(): void {
    this.modal(
      "THE KING'S ORDERS",
      "Runners never attack towers. Build on open ground to lengthen their route, but one road must always remain.\n\nSelect a tower, place it on a legal tile, then upgrade or change its targeting. Survive every assault and preserve the keep.",
    );
  }

  private showSettings(): void {
    const save = loadSave();
    const center = this.scale.width / 2;
    const middle = this.scale.height / 2;
    const panelHeight = Math.min(690, this.scale.height - 20);
    const panelTop = middle - panelHeight / 2;
    const step = Math.min(52, (panelHeight - 180) / 10);
    const firstControlY = panelTop + 100;
    const shade = this.add
      .rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0.72)
      .setOrigin(0)
      .setInteractive()
      .setDepth(100);
    const panel = this.add
      .rectangle(center, middle, Math.min(560, this.scale.width - 32), panelHeight, COLORS.panel)
      .setStrokeStyle(3, ROYAL_GOLD)
      .setDepth(101);
    const heading = this.add
      .text(center, panelTop + 28, "SETTINGS & CAMPAIGN SAVE", {
        fontFamily: "Georgia, serif",
        fontSize: "25px",
        color: "#f3c656",
      })
      .setOrigin(0.5)
      .setDepth(102);
    const status = this.add
      .text(center, panelTop + 61, "", {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "13px",
        color: "#d9ccb2",
        align: "center",
      })
      .setOrigin(0.5)
      .setDepth(102);
    const refresh = (): void => {
      status.setText(
        `Sound ${save.settings.masterVolume > 0 ? "on" : "off"}  •  Contrast ${save.settings.highContrast ? "on" : "off"}  •  UI ${Math.round(save.settings.uiScale * 100)}%\nReduced particles ${save.settings.reducedParticles ? "on" : "off"}  •  Reduced shake ${save.settings.reducedShake ? "on" : "off"}  •  Auto-pause ${save.settings.pauseWhenHidden ? "on" : "off"}`,
      );
    };
    const sound = addButton(this, center, firstControlY, 270, "Toggle Sound", () => {
      save.settings.masterVolume = save.settings.masterVolume > 0 ? 0 : 0.8;
      writeSave(save);
      refresh();
    }).setDepth(103);
    const contrast = addButton(
      this,
      center,
      firstControlY + step,
      270,
      "Toggle High Contrast",
      () => {
        save.settings.highContrast = !save.settings.highContrast;
        document.body.classList.toggle("high-contrast", save.settings.highContrast);
        writeSave(save);
        refresh();
      },
    ).setDepth(103);
    const effects = addButton(
      this,
      center,
      firstControlY + step * 2,
      270,
      "Toggle Reduced Particles",
      () => {
        save.settings.reducedParticles = !save.settings.reducedParticles;
        writeSave(save);
        refresh();
      },
    ).setDepth(103);
    const shake = addButton(
      this,
      center,
      firstControlY + step * 3,
      270,
      "Toggle Reduced Shake",
      () => {
        save.settings.reducedShake = !save.settings.reducedShake;
        writeSave(save);
        refresh();
      },
    ).setDepth(103);
    const uiScale = addButton(this, center, firstControlY + step * 4, 270, "Cycle UI Scale", () => {
      const supportedScales: readonly number[] = [0.9, 1, 1.1, 1.25];
      const currentIndex = supportedScales.findIndex(
        (scale: number): boolean => Math.abs(scale - save.settings.uiScale) < 0.001,
      );
      const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % supportedScales.length : 1;
      save.settings.uiScale = supportedScales[nextIndex] ?? 1;
      writeSave(save);
      refresh();
    }).setDepth(103);
    const autoPause = addButton(
      this,
      center,
      firstControlY + step * 5,
      270,
      "Toggle Auto-Pause",
      () => {
        save.settings.pauseWhenHidden = !save.settings.pauseWhenHidden;
        writeSave(save);
        refresh();
      },
    ).setDepth(103);
    const sync = addButton(
      this,
      center,
      firstControlY + step * 6,
      270,
      "Synchronize Cloud Save",
      () => {
        status.setText("Synchronizing with LittleCastle…");
        void synchronizeDefensePlatform(true).then((result) => status.setText(result.detail));
      },
    ).setDepth(103);
    const exported = addButton(
      this,
      center,
      firstControlY + step * 7,
      270,
      "Export Campaign Save",
      () => {
        const blob = new Blob([exportSaveData()], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `littlecastle-defense-save-${Date.now()}.json`;
        anchor.click();
        URL.revokeObjectURL(url);
        status.setText("Campaign save exported.");
      },
    ).setDepth(103);
    const importCampaignSave = (): void => {
      const input: HTMLInputElement = window.document.createElement("input");
      input.type = "file";
      input.accept = "application/json,.json";
      input.onchange = (): void => {
        const files: FileList | null = input.files;
        if (files === null || files.length === 0) return;
        const file: File | null = files.item(0);
        if (file === null) return;
        const reader: FileReader = new window.FileReader();
        reader.onload = (): void => {
          const contents: string = typeof reader.result === "string" ? reader.result : "";
          const result = importSaveData(contents);
          status.setText(
            result.ok ? "Save imported. Reloading…" : `Import failed: ${result.error}`,
          );
          if (result.ok) window.location.reload();
        };
        reader.onerror = (): void => {
          status.setText("Import failed: the selected file could not be read.");
        };
        reader.readAsText(file);
      };
      input.click();
    };
    const imported: Phaser.GameObjects.Container = addButton(
      this,
      center,
      firstControlY + step * 8,
      270,
      "Import Campaign Save",
      importCampaignSave,
    ).setDepth(103);
    let resetArmed = false;
    const reset = addButton(
      this,
      center,
      firstControlY + step * 9,
      270,
      "Reset Campaign Save",
      () => {
        if (!resetArmed) {
          resetArmed = true;
          const label = reset.getAt(1);
          if (label instanceof Phaser.GameObjects.Text) label.setText("Confirm Permanent Reset");
          status.setText("Press Reset again to erase local campaign progress.");
          return;
        }
        status.setText("Resetting local and cloud campaign saves…");
        void resetDefenseCampaign().then((result) => {
          if (result.ok) window.location.reload();
          else status.setText(result.detail);
        });
      },
    ).setDepth(103);
    const close = addButton(this, center, panelTop + panelHeight - 28, 150, "Close", () => {
      [
        shade,
        panel,
        heading,
        status,
        sound,
        contrast,
        effects,
        shake,
        uiScale,
        autoPause,
        sync,
        exported,
        imported,
        reset,
        close,
      ].forEach((item) => item.destroy());
    }).setDepth(103);
    refresh();
  }

  private modal(heading: string, body: string): void {
    const shade = this.add
      .rectangle(0, 0, this.scale.width, this.scale.height, 0x000000, 0.72)
      .setOrigin(0)
      .setInteractive()
      .setDepth(100);
    const panel = this.add
      .rectangle(
        this.scale.width / 2,
        this.scale.height / 2,
        Math.min(580, this.scale.width - 32),
        320,
        COLORS.panel,
      )
      .setStrokeStyle(3, ROYAL_GOLD)
      .setDepth(101);
    const h = this.add
      .text(this.scale.width / 2, this.scale.height / 2 - 110, heading, {
        fontFamily: "Georgia, serif",
        fontSize: "25px",
        color: "#f3c656",
      })
      .setOrigin(0.5)
      .setDepth(102);
    const text = this.add
      .text(this.scale.width / 2, this.scale.height / 2, body, {
        fontFamily: "Trebuchet MS, sans-serif",
        fontSize: "16px",
        color: Phaser.Display.Color.IntegerToColor(PARCHMENT).rgba,
        align: "center",
        wordWrap: { width: Math.min(500, this.scale.width - 70) },
      })
      .setOrigin(0.5)
      .setDepth(102);
    const close = addButton(
      this,
      this.scale.width / 2,
      this.scale.height / 2 + 120,
      150,
      "Close",
      () => {
        [shade, panel, h, text, close].forEach((item) => item.destroy());
      },
    ).setDepth(103);
  }
}
